import {endOfDay, parseISO} from "date-fns";
import type {Subscription} from "@calendall/shared";

type SubscriptionDateFields = Pick<Subscription, "startDate" | "activeUntil" | "paidUntil">;

const STATUS_LABEL_KEYS = {
  renewing: "DETAIL_SUB_STATUS_RENEWING",
  cancellationScheduled: "DETAIL_SUB_STATUS_CANCELLATION_SCHEDULED",
  paymentActionRequired: "DETAIL_SUB_STATUS_PAYMENT_ACTION_REQUIRED",
  canceled: "DETAIL_SUB_STATUS_CANCELED",
  replaced: "DETAIL_SUB_STATUS_REPLACED",
  synchronizing: "DETAIL_SUB_STATUS_SYNCHRONIZING",
} as const satisfies Record<SubscriptionLifecycleStatus, string>;

export function subscriptionStatusLabelKey(status: SubscriptionLifecycleStatus): string {
  return STATUS_LABEL_KEYS[status];
}

export function getSubscriptionDisplayEndDate(subscription: SubscriptionDateFields): Date {
  // Fall back to activeUntil for legacy subscriptions not yet migrated to paidUntil
  const dateString = subscription.paidUntil ?? subscription.activeUntil;
  return dateString ? parseISO(dateString) : new Date();
}

export function getSubscriptionAccessEndDate(subscription: SubscriptionDateFields): Date {
  return endOfDay(parseISO(subscription.activeUntil));
}

export function hasActiveSubscriptionAccess(subscription: SubscriptionDateFields, now: Date): boolean {
  return getSubscriptionAccessEndDate(subscription).getTime() >= now.getTime();
}

export type SubscriptionLifecycleStatus =
  | "renewing"
  | "cancellationScheduled"
  | "paymentActionRequired"
  | "canceled"
  | "replaced"
  /** Stripe-linked, but written before lifecycle facts were recorded. */
  | "synchronizing";

/** Dates are calendar days (yyyy-MM-dd). */
export interface SubscriptionLifecycleSummary {
  /** Absent for a subscription without a Stripe link. */
  status?: SubscriptionLifecycleStatus;
  paidThrough: string;
  /** Present only when access ends on a different day than the paid period. */
  accessThrough?: string;
  cancelsOn?: string;
  /** A failed payment shown next to a status that is not itself the payment failure, such as a scheduled cancellation. */
  paymentActionRequired?: boolean;
}

/** Derived from the explicit Stripe lifecycle, not from the presence of Stripe data. */
export function getSubscriptionLifecycleSummary(
  subscription: Pick<Subscription, "activeUntil" | "paidUntil" | "autoRenewal">,
): SubscriptionLifecycleSummary {
  const paidThrough = subscription.paidUntil ?? subscription.activeUntil;
  const dates = {
    paidThrough,
    ...(subscription.activeUntil !== paidThrough && {accessThrough: subscription.activeUntil}),
  };
  const autoRenewal = subscription.autoRenewal;
  if (!autoRenewal) return dates;

  const lifecycle = autoRenewal.lifecycle;
  if (!lifecycle) return {status: "synchronizing", ...dates};

  const renewal = lifecycle.renewal;
  switch (renewal.state) {
    case "renewing":
      return {status: lifecycle.payment.state === "actionRequired" ? "paymentActionRequired" : "renewing", ...dates};
    case "cancellationScheduled":
      return {
        status: "cancellationScheduled",
        ...dates,
        cancelsOn: renewal.cancelsOn,
        ...(lifecycle.payment.state === "actionRequired" && {paymentActionRequired: true}),
      };
    case "canceled":
      return {status: "canceled", ...dates, ...(lifecycle.payment.state === "actionRequired" && {paymentActionRequired: true})};
    case "beingReplaced":
    case "replaced":
      return {status: "replaced", ...dates};
  }
}
